import { motion, useReducedMotion } from "motion/react";

export type ThoughtBubbleMood = "thinking" | "retrieving" | "happy" | "done";

export interface ThoughtBubbleProps {
  mood: ThoughtBubbleMood;
  size?: number;
  /** Extra bounce/wiggle on hover — the header row is already clickable. */
  interactive?: boolean;
}

const FILL = "var(--color-accent, #10805c)";
const INK = "var(--color-ink, #16181b)";
const HIGHLIGHT = "var(--color-surface, #ffffff)";

/**
 * Animated thought-bubble mascot for the compact embed work timeline.
 * Replaces the chevron while streaming to feel more alive during retrieval.
 */
export function ThoughtBubble({ mood, size = 24, interactive = true }: ThoughtBubbleProps) {
  const reduce = useReducedMotion() ?? false;
  const retrieving = mood === "retrieving";
  const happy = mood === "happy";
  const thinking = mood === "thinking";
  const done = mood === "done";

  const bob = reduce
    ? {}
    : retrieving
      ? { y: [0, -3, 0], scale: [1, 1.06, 1] }
      : happy
        ? { y: [0, -4, 0], rotate: [0, -4, 4, 0] }
        : thinking
          ? { y: [0, -2, 0] }
          : { y: 0 };

  const bobTransition = reduce
    ? { duration: 0 }
    : retrieving
      ? { duration: 0.55, repeat: Infinity, ease: "easeInOut" as const }
      : happy
        ? { duration: 0.45, ease: "easeOut" as const }
        : thinking
          ? { duration: 1.8, repeat: Infinity, ease: "easeInOut" as const }
          : { duration: 0.2 };

  const hover = interactive && !reduce
    ? { scale: 1.08, rotate: retrieving ? 0 : [-2, 2, -2, 0] }
    : undefined;

  const label =
    mood === "retrieving"
      ? "Searching for information"
      : mood === "happy"
        ? "Found useful data"
        : mood === "thinking"
          ? "Thinking"
          : "Work complete";

  return (
    <motion.span
      role="img"
      aria-label={label}
      animate={bob}
      transition={bobTransition}
      whileHover={hover}
      whileTap={interactive && !reduce ? { scale: 0.92 } : undefined}
      style={{ display: "inline-flex", lineHeight: 0, flexShrink: 0 }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 40 36"
        width={size}
        height={size * (36 / 40)}
        aria-hidden
      >
        {/* trailing bubbles */}
        <circle cx="6" cy="30" r="2.2" fill={FILL} opacity={0.55} />
        <circle cx="11" cy="33" r="1.5" fill={FILL} opacity={0.4} />
        {/* main cloud */}
        <path
          d="M8 10 h24 a8 8 0 0 1 8 8 v6 a7 7 0 0 1-7 7 H14 a7 7 0 0 1-7-7 v-6 a8 8 0 0 1 8-8 z"
          fill={FILL}
          opacity={0.92}
        />
        {/* face — eyes */}
        {happy || retrieving ? (
          <>
            <path
              d="M14 17 Q17 14 20 17"
              fill="none"
              stroke={HIGHLIGHT}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M22 17 Q25 14 28 17"
              fill="none"
              stroke={HIGHLIGHT}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </>
        ) : (
          <>
            <circle cx="17" cy="17" r="1.6" fill={HIGHLIGHT} />
            <circle cx="25" cy="17" r="1.6" fill={HIGHLIGHT} />
            {thinking && (
              <circle cx="17" cy="16.2" r="0.7" fill={INK} opacity={0.35} />
            )}
          </>
        )}
        {/* mouth */}
        {happy || retrieving ? (
          <path
            d="M15.5 22 Q21 26.5 26.5 22"
            fill="none"
            stroke={HIGHLIGHT}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        ) : done ? (
          <path
            d="M16 22 Q21 24 26 22"
            fill="none"
            stroke={HIGHLIGHT}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        ) : (
          <circle cx="21" cy="22" r="1.1" fill={HIGHLIGHT} opacity={0.85} />
        )}
        {/* thinking dots — float above the cloud */}
        {thinking &&
          [0, 1, 2].map((i) => (
            <motion.circle
              key={i}
              cx={14 + i * 4.5}
              cy={5.5}
              r={1.1}
              fill={FILL}
              initial={{ opacity: reduce ? 0.7 : 0.25 }}
              animate={reduce ? { opacity: 0.7 } : { opacity: [0.25, 1, 0.25], y: [0, -1.5, 0] }}
              transition={
                reduce
                  ? { duration: 0 }
                  : { duration: 1.1, repeat: Infinity, delay: i * 0.16, ease: "easeInOut" }
              }
            />
          ))}
        {/* excitement sparkles when retrieving / found */}
        {(retrieving || happy) &&
          [
            { cx: 32, cy: 8, r: 1.4 },
            { cx: 35, cy: 13, r: 1.0 },
            { cx: 5, cy: 12, r: 0.9 },
          ].map((s, i) => (
            <motion.circle
              key={`spark-${i}`}
              cx={s.cx}
              cy={s.cy}
              r={s.r}
              fill={HIGHLIGHT}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={
                reduce
                  ? { opacity: 0.8, scale: 1 }
                  : { opacity: [0.2, 1, 0.2], scale: [0.6, 1.2, 0.6] }
              }
              transition={
                reduce
                  ? { duration: 0 }
                  : { duration: 0.7, repeat: Infinity, delay: i * 0.12, ease: "easeInOut" }
              }
            />
          ))}
      </svg>
    </motion.span>
  );
}

/** Map work-log entries to the bubble's expression while a turn streams. */
export function moodFromWorkEntries(
  entries: { kind: string; status?: string; results?: unknown[] }[],
  streaming: boolean,
): ThoughtBubbleMood {
  if (!streaming) return "done";
  const running = entries.some((e) => e.kind === "tool" && e.status === "running");
  if (running) return "retrieving";
  const lastTool = [...entries].reverse().find((e) => e.kind === "tool") as
    | { kind: "tool"; status?: string; results?: unknown[] }
    | undefined;
  if (lastTool?.status === "done" && (lastTool.results?.length ?? 0) > 0) {
    return "happy";
  }
  return "thinking";
}

/** Headline copy paired with the bubble mood (compact embed). */
export function headlineForMood(
  mood: ThoughtBubbleMood,
  streaming: boolean,
  dur: string | null,
  stepsSuffix: string,
): string {
  if (!streaming) {
    return (dur ? `Thought for ${dur}` : "Worked") + stepsSuffix;
  }
  switch (mood) {
    case "retrieving":
      return "Fetching data…";
    case "happy":
      return "Found it!";
    case "thinking":
      return "Thinking…";
    default:
      return "Working…";
  }
}
